import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Settings2 } from "lucide-react";
import { API, authHeaders } from "./shared";
import { fmtDateTime } from "@/lib/datetime";

const GROUPS = [
  { title: "Company-Wide Package Pricing (base rental)", fields: [
    ["three_day_package", "3-Day Special $ (minimum rental)", "The public entry offer — vehicle rental only"],
    ["additional_day_rate", "Additional-Day Rate $/day", "Every day after the 3-day or weekly package"],
    ["weekly_special", "Weekly Special $ (7 days)", "Best Value — replaces the standard progression for 7-day bookings"],
  ]},
  { title: "Protection & Coverage", fields: [
    ["allin_coverage_day", "All-In Coverage $/day (company-wide rate)", "Flat rate for every vehicle — the actual third-party insurance charge. Only a per-booking management override changes it"],
    ["protection_cost_day", "Protection COST $/day (what coverage costs us)", "What AutoHire actually pays per covered day — the system uses this to compute Protection Profit/Margin ($25 collected − $20 cost = $5 profit)"],
    ["damage_waiver_day", "Damage Waiver $/day", "Always a separate line item — never bundled into the rental price"],
  ]},
  { title: "Driver Fees", fields: [
    ["young_driver_day", "Young Driver Fee $/day (ages 21–24)", ""],
    ["additional_driver_day", "Additional Driver Fee $/day", ""],
  ]},
  { title: "Service Fees (flat)", fields: [
    ["delivery_fee", "Delivery Fee $", ""],
    ["airport_fee", "Airport Delivery Fee $", ""],
    ["cleaning_fee", "Cleaning Charge $", ""],
    ["fuel_charge", "Fuel Charge $", ""],
  ]},
  { title: "Late Return & Extensions", fields: [
    ["late_fee_hourly", "Late Return Fee $/hour", "Applies after the grace period, per your agreement"],
    ["late_fee_grace_minutes", "Grace period (minutes)", ""],
  ]},
];

export default function PricingSettingsModule({ token }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/pricing/settings`, authHeaders(token)).then((r) => setS(r.data)).catch(() => toast.error("Could not load pricing settings."));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s };
      delete body.updated_at; delete body.updated_by;
      Object.keys(body).forEach((k) => { if (typeof body[k] === "string" && !["tax_applies_to", "extension_rule", "convenience_fee_type"].includes(k)) body[k] = Number(body[k]) || 0; });
      const { data } = await axios.patch(`${API}/pricing/settings`, body, authHeaders(token));
      setS(data);
      toast.success("Pricing settings saved. New bookings use these rates immediately.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading pricing…</div>;

  const input = (k, label, hint) => (
    <label key={k} className="text-xs text-white/50 uppercase tracking-wider block">{label}
      <input data-testid={`pricing-input-${k}`} type="number" step="0.01" min="0" value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)}
        className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
      {hint && <span className="block text-white/30 text-[10px] normal-case mt-0.5">{hint}</span>}
    </label>
  );

  return (
    <div data-testid="pricing-settings" className="space-y-5">
      <CardFeeSettings token={token} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-white flex items-center gap-2"><Settings2 size={17} className="text-[#F2B705]" /> Management Pricing Settings</h3>
          <p className="text-white/45 text-sm mt-0.5">Every rate is management-controlled — no code changes needed. Each item is billed as its own line so you know exactly where every dollar comes from.</p>
        </div>
        <button data-testid="pricing-save-btn" onClick={save} disabled={saving}
          className="flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50 shrink-0">
          <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
          <h4 className="text-white font-semibold text-sm mb-4">{g.title}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{g.fields.map(([k, l, h]) => input(k, l, h))}</div>
        </div>
      ))}

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="pricing-convenience-card">
        <h4 className="text-white font-semibold text-sm mb-1">Convenience Fee</h4>
        <p className="text-white/40 text-xs mb-4">Optional booking fee — recover payment-processing costs without changing rental pricing. Applies automatically to every new booking when enabled.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-end">
          <label className="text-xs text-white/50 uppercase tracking-wider block">Fee Type
            <select data-testid="pricing-convenience-type" value={s.convenience_fee_type || "off"} onChange={(e) => set("convenience_fee_type", e.target.value)}
              className="mt-1 w-full bg-[#0C0C0C] border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm">
              <option value="off">Off</option>
              <option value="flat">Flat $ per booking</option>
              <option value="percent">% of subtotal</option>
            </select>
          </label>
          <label className="text-xs text-white/50 uppercase tracking-wider block">{(s.convenience_fee_type === "percent") ? "Percentage %" : "Amount $"}
            <input data-testid="pricing-convenience-value" type="number" step="0.01" min="0" value={s.convenience_fee_value ?? ""} onChange={(e) => set("convenience_fee_value", e.target.value)}
              className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
          </label>
        </div>
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="pricing-tax-card">
        <h4 className="text-white font-semibold text-sm mb-1">Sales Tax</h4>
        <p className="text-white/40 text-xs mb-4">Off by default. When you're ready to collect sales tax, flip it on here — no code changes required.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer">
            <input data-testid="pricing-tax-enabled" type="checkbox" checked={!!s.tax_enabled} onChange={(e) => set("tax_enabled", e.target.checked)}
              className="w-4 h-4 accent-[#F2B705]" />
            Sales Tax Enabled
          </label>
          <label className="text-xs text-white/50 uppercase tracking-wider block">Tax Percentage %
            <input data-testid="pricing-tax-rate" type="number" step="0.01" min="0" value={s.tax_rate_pct ?? ""} onChange={(e) => set("tax_rate_pct", e.target.value)}
              className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
          </label>
          <label className="text-xs text-white/50 uppercase tracking-wider block">Tax Applies To
            <select data-testid="pricing-tax-applies" value={s.tax_applies_to || "rental"} onChange={(e) => set("tax_applies_to", e.target.value)}
              className="mt-1 w-full bg-[#0C0C0C] border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm">
              <option value="rental">Vehicle Rental Only</option>
              <option value="rental_plus_options">Rental + Optional Products</option>
              <option value="all">Entire Booking</option>
            </select>
          </label>
        </div>
      </div>

      <TpiCard token={token} />

      <div className="text-white/30 text-xs">
        All-In Coverage bills at the company-wide rate above for every vehicle. The only exception: a per-booking override applied by management from the booking's Pricing Breakdown panel.
        Weekly Special landing-page controls (featured vehicle, pixels, countdown, performance report) live in <span className="text-[#F2B705] font-semibold">Marketing → Weekly Special</span>.
        {s.updated_at && <span className="block mt-1">Last updated {fmtDateTime(s.updated_at)}{s.updated_by ? ` by ${s.updated_by}` : ""}</span>}
      </div>
    </div>
  );
}

function TpiCard({ token }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/pricing/third-party-insurance`, authHeaders(token)).then((r) => setT(r.data)).catch(() => {});
  }, [token]);
  if (!t) return null;
  const set = (k, v) => setT((s) => ({ ...s, [k]: v }));
  const save = async (enable) => {
    setBusy(true);
    try {
      const body = { ...t, price_per_day: Number(t.price_per_day) || 0 };
      if (enable !== undefined) body.enabled = enable;
      const { data } = await axios.patch(`${API}/pricing/third-party-insurance`, body, authHeaders(token));
      setT(data);
      toast.success(data.enabled ? "Option 2 (third-party insurance) is now LIVE on checkout." : "Saved. Option 2 stays 'Currently Unavailable' until enabled.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  const inp = "w-full bg-[#0A0A0A] border border-white/15 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#F2B705]/60";
  return (
    <div data-testid="tpi-settings-card" className="rounded-lg border border-white/10 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold">Option 2 — Optional Third-Party Rental Insurance</p>
          <p className="text-white/40 text-xs mt-0.5">Checkout shows this option as "Currently Unavailable" until a provider is configured and enabled here. AutoHire is never the insurer.</p>
        </div>
        <span className={`text-[10px] font-black px-2 py-0.5 rounded border ${t.enabled ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-white/25 text-white/50"}`}>{t.enabled ? "LIVE ON CHECKOUT" : "CURRENTLY UNAVAILABLE"}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-3">
        <label className="text-[10px] text-white/45">Insurance provider *<input data-testid="tpi-provider" className={inp} value={t.provider || ""} onChange={(e) => set("provider", e.target.value)} placeholder="e.g., Sure / Bonzah" /></label>
        <label className="text-[10px] text-white/45">Price per day ($) *<input data-testid="tpi-price" type="number" className={inp} value={t.price_per_day || ""} onChange={(e) => set("price_per_day", e.target.value)} /></label>
        <label className="text-[10px] text-white/45">Coverage type<input data-testid="tpi-type" className={inp} value={t.coverage_type || ""} onChange={(e) => set("coverage_type", e.target.value)} placeholder="e.g., Liability + CDW" /></label>
        <label className="text-[10px] text-white/45">Coverage limits<input data-testid="tpi-limits" className={inp} value={t.coverage_limits || ""} onChange={(e) => set("coverage_limits", e.target.value)} placeholder="e.g., $25k/$50k/$10k" /></label>
        <label className="text-[10px] text-white/45">Major exclusions<input data-testid="tpi-exclusions" className={inp} value={t.exclusions || ""} onChange={(e) => set("exclusions", e.target.value)} placeholder="e.g., DUI, prohibited use" /></label>
        <label className="text-[10px] text-white/45">Policy documents URL<input data-testid="tpi-doc" className={inp} value={t.policy_doc_url || ""} onChange={(e) => set("policy_doc_url", e.target.value)} placeholder="https://…" /></label>
      </div>
      <div className="flex gap-2 mt-3">
        <button data-testid="tpi-save" onClick={() => save()} disabled={busy} className="text-xs font-bold border border-white/20 text-white/70 px-3 py-1.5 rounded hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">Save Details</button>
        {t.enabled ? (
          <button data-testid="tpi-disable" onClick={() => save(false)} disabled={busy} className="text-xs font-bold border border-red-400/40 text-red-300 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white disabled:opacity-50">Disable Option 2</button>
        ) : (
          <button data-testid="tpi-enable" onClick={() => save(true)} disabled={busy} className="text-xs font-bold bg-[#F2B705] text-black px-3 py-1.5 rounded hover:bg-white disabled:opacity-50">Enable on Checkout</button>
        )}
      </div>
    </div>
  );
}

function CardFeeSettings({ token }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/payments/fee-settings`, authHeaders(token)).then((r) => setCfg(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [token]);
  if (!cfg) return null;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/payments/fee-settings`, {
        percent: parseFloat(cfg.percent) || 0,
        fixed_cents: parseInt(cfg.fixed_cents, 10) || 0,
      }, authHeaders(token));
      setCfg(data);
      toast.success("Processing fee settings saved — applied to every Square/Stripe card payment going forward.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };
  const due = 301.59;
  const pct = parseFloat(cfg.percent) || 0, fixedCents = parseInt(cfg.fixed_cents, 10) || 0;
  const dueCents = Math.round(due * 100);
  let total = dueCents;
  while (total - Math.round(total * pct / 100 + fixedCents) < dueCents) total += 1;
  const feeCents = total - dueCents;
  const preview = `$${due.toFixed(2)} subtotal → fee $${(feeCents / 100).toFixed(2)}, total $${(total / 100).toFixed(2)}`;
  return (
    <div data-testid="card-fee-settings" className="rounded-xl border border-white/10 bg-[#0C0C0C] p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h4 className="text-white font-bold text-sm">💳 Card Processing Fee (Stripe / Square)</h4>
        <span className="text-xs font-bold px-3 py-1 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
          ALWAYS ON — applies to every Square/Stripe card payment
        </span>
        <button data-testid="card-fee-save" onClick={save} disabled={saving}
          className="ml-auto px-4 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
      </div>
      <p className="text-white/40 text-xs">
        AutoHire application calculation (never a Stripe/Square account setting) — grossed up so AutoHire nets the
        full rental subtotal even after the processor's own cut on the fee itself. Disclosed to the customer BEFORE
        they authorize. Never applied to Cash, Cash App, Zelle, Manual, or other non-card methods. Not renter-editable.
      </p>
      <div className="rounded-lg border border-white/10 p-3 max-w-xs">
        <div className="flex gap-3">
          <label className="text-[10px] text-white/45 uppercase flex-1">Percent %
            <input data-testid="card-fee-percent" type="number" step="0.1" min="0" max="10" value={cfg.percent}
              onChange={(e) => setCfg({ ...cfg, percent: e.target.value })}
              className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-white text-sm" />
          </label>
          <label className="text-[10px] text-white/45 uppercase flex-1">Fixed (cents)
            <input data-testid="card-fee-fixed-cents" type="number" step="1" min="0" max="500" value={cfg.fixed_cents}
              onChange={(e) => setCfg({ ...cfg, fixed_cents: e.target.value })}
              className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-white text-sm" />
          </label>
        </div>
        <div className="text-[10px] text-white/35 mt-1.5">{preview}</div>
      </div>
    </div>
  );
}
